// Company Features: one company's entitlements. Their plan (tier) sets the
// starting point; the operator adds or takes away single features for this
// company (plan_override exceptions, which survive plan changes). The company
// admin's own on/off switch is shown read-only.
//
// Until the registry migration migrations-2026-9-28-app-features has run the
// server answers plans_ready: false and this page falls back to the old
// lock/unlock switch (POST /features/toggle).
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import api from '../api/axios'
import {
  TIERS,
  changeCompanyTier,
  getCompanyFeatures,
  groupFeatures,
  isCore,
  isPlanSchemaMissing,
  previewTierChange,
  setFeatureOverride,
  tierName,
  toggleFeatureUnlocked,
  truthy,
} from '../api/features'
import type { CompanyFeature, PlanOverride, TierPreview } from '../api/features'
import { MigrationCallout, PlanGroup, PlatformTags } from './Plans'

interface Company {
  id: string
  company_name: string
  company_code: string
  subscription_tier?: string
}

const TIER_STYLE: Record<string, { badge: string; dot: string }> = {
  basic:      { badge: 'bg-slate-100 text-slate-600 border-slate-200',  dot: 'bg-slate-400' },
  standard:   { badge: 'bg-blue-50 text-blue-700 border-blue-200',       dot: 'bg-blue-500' },
  premium:    { badge: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
  enterprise: { badge: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
}

const errMsg = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback

type Show = 'all' | 'included' | 'exceptions' | 'not_included' | 'admin_off'

const badge = 'inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap'
const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed'

function broadcastFeaturesUpdated() {
  if (!('BroadcastChannel' in window)) return
  try {
    const ch = new BroadcastChannel('feature-updates')
    ch.postMessage({ type: 'FEATURES_UPDATED' })
    ch.close()
  } catch { /* non-blocking */ }
}

export default function FeatureFlags() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [companies, setCompanies] = useState<Company[]>([])
  const [companiesLoading, setCompaniesLoading] = useState(true)
  const [companySearch, setCompanySearch] = useState('')
  const selectedId = searchParams.get('company')
  const selectedCompany = companies.find((c) => String(c.id) === selectedId) ?? null

  const [features, setFeatures] = useState<CompanyFeature[]>([])
  const [tier, setTier] = useState<string>('standard')
  const [plansReady, setPlansReady] = useState(true)
  const [featuresLoading, setFeaturesLoading] = useState(false)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [show, setShow] = useState<Show>('all')

  const [pendingTier, setPendingTier] = useState<string | null>(null)
  const [preview, setPreview] = useState<TierPreview | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState('')
  const [applyingTier, setApplyingTier] = useState(false)

  const [toast, setToast] = useState<{ msg: string; tone: 'ok' | 'error' } | null>(null)
  const notify = useCallback((msg: string, tone: 'ok' | 'error' = 'ok') => {
    setToast({ msg, tone })
    window.setTimeout(() => setToast(null), 3000)
  }, [])

  const loadCompanies = useCallback(async () => {
    try {
      const res = await api.get('/super-admin/companies')
      setCompanies(res.data.companies || [])
    } catch (err) {
      setError(errMsg(err, 'Failed to fetch companies'))
    } finally {
      setCompaniesLoading(false)
    }
  }, [])

  useEffect(() => { loadCompanies() }, [loadCompanies])

  const loadFeatures = useCallback(async (companyId: string, { quiet = false } = {}) => {
    if (!quiet) {
      setFeaturesLoading(true)
      setFeatures([])
    }
    try {
      const r = await getCompanyFeatures(companyId)
      setFeatures(r.features)
      setPlansReady(r.plans_ready)
      if (r.subscription_tier) setTier(r.subscription_tier)
    } catch (err) {
      setError(errMsg(err, 'Failed to fetch features'))
    } finally {
      setFeaturesLoading(false)
    }
  }, [])

  useEffect(() => {
    setPendingTier(null)
    setPreview(null)
    setError('')
    if (selectedId) loadFeatures(selectedId)
  }, [selectedId, loadFeatures])

  // Keep the header tier right before the features call answers.
  useEffect(() => {
    if (selectedCompany?.subscription_tier) setTier(selectedCompany.subscription_tier)
  }, [selectedCompany?.id, selectedCompany?.subscription_tier])

  const selectCompany = (c: Company) => {
    setSearch('')
    setShow('all')
    setSearchParams({ company: String(c.id) })
  }

  // ── Plan change preview ───────────────────────────────────────────────
  const pickTier = async (next: string) => {
    if (!selectedId) return
    if (next === tier) {
      setPendingTier(null)
      setPreview(null)
      return
    }
    setPendingTier(next)
    setPreview(null)
    setPreviewError('')
    if (!plansReady) return
    setPreviewLoading(true)
    try {
      setPreview(await previewTierChange(selectedId, next))
    } catch (err) {
      if (isPlanSchemaMissing(err)) setPlansReady(false)
      else setPreviewError(errMsg(err, "Couldn't preview the plan change"))
    } finally {
      setPreviewLoading(false)
    }
  }

  const applyTier = async () => {
    if (!selectedId || !pendingTier) return
    setApplyingTier(true)
    try {
      await changeCompanyTier(selectedId, pendingTier)
      notify(`Moved to ${tierName(pendingTier)}.`)
      setPendingTier(null)
      setPreview(null)
      broadcastFeaturesUpdated()
      await Promise.all([loadCompanies(), loadFeatures(selectedId, { quiet: true })])
    } catch (err) {
      notify(errMsg(err, "Couldn't change the plan"), 'error')
    } finally {
      setApplyingTier(false)
    }
  }

  // ── Per-feature changes ───────────────────────────────────────────────
  const override = async (f: CompanyFeature, value: PlanOverride, done: string) => {
    if (!selectedId || busyKey) return
    setBusyKey(f.feature_key)
    try {
      await setFeatureOverride(selectedId, f.feature_key, value)
      notify(done)
      broadcastFeaturesUpdated()
      await loadFeatures(selectedId, { quiet: true })
    } catch (err) {
      if (isPlanSchemaMissing(err)) {
        setPlansReady(false)
        await loadFeatures(selectedId, { quiet: true })
      } else notify(errMsg(err, "Couldn't change the feature"), 'error')
    } finally {
      setBusyKey(null)
    }
  }

  const legacyToggle = async (f: CompanyFeature) => {
    if (!selectedId || busyKey) return
    const next = !truthy(f.is_unlocked)
    setBusyKey(f.feature_key)
    try {
      await toggleFeatureUnlocked(selectedId, f.feature_key, next)
      notify(`${f.feature_name} ${next ? 'unlocked' : 'locked'}.`)
      broadcastFeaturesUpdated()
      await loadFeatures(selectedId, { quiet: true })
    } catch (err) {
      notify(errMsg(err, "Couldn't change the feature"), 'error')
    } finally {
      setBusyKey(null)
    }
  }

  // ── Derived ───────────────────────────────────────────────────────────
  const nameOf = useMemo(() => new Map(features.map((f) => [f.feature_key, f.feature_name])), [features])
  const byKey = useMemo(() => new Map(features.map((f) => [f.feature_key, f])), [features])
  const added = features.filter((f) => f.plan_override === 'added' && !truthy(f.in_plan))
  const removed = features.filter((f) => f.plan_override === 'removed')
  const entitled = features.filter((f) => truthy(f.is_unlocked) || isCore(f))
  const adminOff = features.filter((f) => !isCore(f) && truthy(f.is_unlocked) && !truthy(f.is_enabled))

  const filtered = useMemo(() => features.filter((f) => {
    const ent = truthy(f.is_unlocked) || isCore(f)
    if (show === 'included' && !ent) return false
    if (show === 'not_included' && ent) return false
    if (show === 'exceptions' && !(f.plan_override === 'removed' || (f.plan_override === 'added' && !truthy(f.in_plan)))) return false
    if (show === 'admin_off' && !(!isCore(f) && truthy(f.is_unlocked) && !truthy(f.is_enabled))) return false
    if (search) {
      const q = search.toLowerCase()
      return f.feature_name.toLowerCase().includes(q) || (f.description || '').toLowerCase().includes(q) || f.feature_key.includes(q)
    }
    return true
  }), [features, show, search])

  const groups = useMemo(() => {
    if (plansReady) return groupFeatures(filtered)
    // Legacy catalog has no platform / app_group columns: plain categories.
    const m = new Map<string, CompanyFeature[]>()
    for (const f of filtered) m.set(f.category || 'other', [...(m.get(f.category || 'other') || []), f])
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([c, items]) => ({
      key: c, label: c.charAt(0).toUpperCase() + c.slice(1), items,
    }))
  }, [filtered, plansReady])

  const filteredCompanies = companies.filter((c) =>
    c.company_name.toLowerCase().includes(companySearch.toLowerCase()) ||
    c.company_code.toLowerCase().includes(companySearch.toLowerCase())
  )

  // ── Cells ─────────────────────────────────────────────────────────────
  const includedBecause = (f: CompanyFeature) => {
    if (!plansReady) {
      return truthy(f.is_unlocked)
        ? <span className={`${badge} bg-emerald-50 text-emerald-700`}>Unlocked</span>
        : <span className="text-xs text-slate-400">Locked</span>
    }
    if (isCore(f)) return <span className={`${badge} bg-slate-50 text-slate-600 border border-slate-200`}>Always on</span>
    if (f.plan_override === 'removed') return <span className={`${badge} bg-red-50 text-red-700`}>Removed for this company</span>
    if (f.plan_override === 'added' && !truthy(f.in_plan)) return <span className={`${badge} bg-violet-50 text-violet-700`}>Added for this company</span>
    if (truthy(f.in_plan)) return <span className={`${badge} bg-slate-50 text-slate-600 border border-slate-200`}>In {tierName(tier)}</span>
    return <span className="text-xs text-slate-400">Not included</span>
  }

  const theirAdmin = (f: CompanyFeature) => {
    if (isCore(f)) return <span className="text-xs text-slate-400">Always on</span>
    if (!truthy(f.is_unlocked)) return <span className="text-xs text-slate-400">—</span>
    if (!truthy(f.is_enabled)) return <span className={`${badge} bg-amber-50 text-amber-700`}>Turned off</span>
    const parent = f.parent_key ? byKey.get(f.parent_key) : null
    if (parent && !truthy(f.is_active)) {
      return <span className={`${badge} bg-amber-50 text-amber-700`} title={`${parent.feature_name} is off, so this is off too`}>Off with {parent.feature_name}</span>
    }
    return <span className={`${badge} bg-emerald-50 text-emerald-700`}>On</span>
  }

  const action = (f: CompanyFeature) => {
    const busy = busyKey === f.feature_key
    const spin = busy ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : null
    const disabled = !!busyKey || applyingTier
    if (!plansReady) {
      const on = truthy(f.is_unlocked)
      return <button className={btn} disabled={disabled} onClick={() => legacyToggle(f)}>{spin}{on ? 'Lock' : 'Unlock'}</button>
    }
    if (isCore(f)) return null
    if (f.plan_override === 'removed') {
      return <button className={btn} disabled={disabled} onClick={() => override(f, null, `${f.feature_name} given back.`)}>{spin}Give it back</button>
    }
    if (f.plan_override === 'added' && !truthy(f.in_plan)) {
      return <button className={btn} disabled={disabled} onClick={() => override(f, null, `${f.feature_name} extra removed.`)}>{spin}Remove extra</button>
    }
    if (truthy(f.in_plan)) {
      return <button className={btn} disabled={disabled} onClick={() => override(f, 'removed', `${f.feature_name} taken away.`)}>{spin}Take away</button>
    }
    return <button className={btn} disabled={disabled} onClick={() => override(f, 'added', `${f.feature_name} added for this company.`)}>{spin}Add for this company</button>
  }

  const listNames = (keys: CompanyFeature[]) => keys.map((f) => f.feature_name).join(', ')
  const previewSentence = (p: TierPreview) => {
    const gains = p.gains.map((g) => g.feature_name).join(', ')
    const losses = p.losses.map((g) => g.feature_name).join(', ')
    if (!gains && !losses) return "wouldn't change any features."
    if (gains && losses) return `would add: ${gains}, and take away: ${losses}.`
    return gains ? `would add: ${gains}.` : `would take away: ${losses}.`
  }
  const keepSentence = () => {
    const kept = [
      added.length ? `${added.length === 1 ? 'extra' : 'extras'} (${listNames(added)})` : '',
      removed.length ? `${removed.length === 1 ? 'removal' : 'removals'} (${listNames(removed)})` : '',
    ].filter(Boolean)
    const tail = "nothing their admin switched off gets switched back on."
    return kept.length ? `Their ${kept.join(' and ')} stay as they are, and ${tail}` : `N${tail.slice(1)}`
  }

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4">
          <h1 className="text-xl font-bold text-slate-900">Company Features</h1>
          <p className="text-sm text-slate-500">
            What each company can use: their plan, plus anything you add or take away for them. Plan contents are set on{' '}
            <Link to="/plans" className="text-blue-600 hover:underline">Plans</Link>.
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError('')} aria-label="Dismiss"><XMarkIcon className="w-4 h-4 text-red-400 hover:text-red-600" /></button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-6">
          {/* Company picker */}
          <div className="flex flex-col gap-3">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search companies…"
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
            {companiesLoading ? (
              <div className="flex items-center justify-center py-16 text-slate-400">
                <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" /><span className="text-sm">Loading…</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto pr-0.5">
                {filteredCompanies.map((c) => {
                  const t = c.subscription_tier || 'standard'
                  const ts = TIER_STYLE[t] ?? TIER_STYLE.standard
                  const isSelected = selectedCompany?.id === c.id
                  const pct = isSelected && features.length > 0 ? Math.round((entitled.length / features.length) * 100) : null
                  return (
                    <button
                      key={c.id}
                      onClick={() => selectCompany(c)}
                      className={`w-full text-left bg-white rounded-lg border p-3.5 transition-all hover:shadow-sm ${
                        isSelected ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{c.company_name}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{c.company_code}</p>
                        </div>
                        <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${ts.badge}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${ts.dot}`} />
                          {tierName(t)}
                        </span>
                      </div>
                      {pct !== null && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                            <span>{entitled.length} of {features.length} features included</span>
                            <span>{pct}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* One company */}
          <div className="min-w-0">
            {!selectedId ? (
              <div className="bg-white rounded-lg border border-slate-200 flex flex-col items-center justify-center py-28 px-8 text-center">
                <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                  <FlagIcon className="w-7 h-7 text-slate-400" />
                </div>
                <h2 className="text-base font-semibold text-slate-900 mb-1">Select a company</h2>
                <p className="text-sm text-slate-500 max-w-xs">Choose a company to see what it has and why.</p>
              </div>
            ) : featuresLoading ? (
              <div className="bg-white rounded-lg border border-slate-200 flex items-center justify-center py-28">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2.5">
                  <h2 className="text-xl font-bold text-slate-900">{selectedCompany?.company_name ?? 'Company'}</h2>
                  {selectedCompany && (
                    <span className={`${badge} bg-slate-50 text-slate-600 border border-slate-200`}>{selectedCompany.company_code}</span>
                  )}
                  <label className="ml-auto flex items-center gap-1.5 text-sm text-slate-700">
                    Plan
                    <select
                      value={pendingTier ?? tier}
                      onChange={(e) => pickTier(e.target.value)}
                      disabled={applyingTier}
                      className="px-2 py-1 text-[13px] border border-slate-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      {TIERS.map((t) => <option key={t} value={t}>{tierName(t)}</option>)}
                    </select>
                  </label>
                </div>
                <p className="text-sm text-slate-500 mt-1 mb-4 max-w-3xl">
                  Their plan sets the starting point. Add a feature they haven't paid a higher plan for, or take one away.
                  Their admin's own on/off switches are shown but only they change them.
                </p>

                {!plansReady && (
                  <div className="mb-3">
                    <MigrationCallout>
                      Plans aren't set up on this server yet, so this page uses the old lock and unlock switch.
                    </MigrationCallout>
                  </div>
                )}

                {pendingTier && (
                  <div className="flex items-start gap-2 p-3 mb-3 rounded-lg bg-blue-50 text-blue-800 text-[13px]">
                    <InformationCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      {!plansReady ? (
                        <p>Moving to <b>{tierName(pendingTier)}</b> applies that tier's feature defaults.</p>
                      ) : previewLoading ? (
                        <p className="inline-flex items-center gap-1.5"><ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> Working out what moving to {tierName(pendingTier)} would change…</p>
                      ) : previewError ? (
                        <p className="text-red-700">{previewError}</p>
                      ) : preview && (
                        <p>
                          Moving to <b>{tierName(pendingTier)}</b> {previewSentence(preview)}{' '}
                          {keepSentence()}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={applyTier}
                          disabled={applyingTier || previewLoading}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          {applyingTier && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />} Apply plan change
                        </button>
                        <button onClick={() => pickTier(tier)} disabled={applyingTier} className={btn}>Cancel</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary + filters */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <div className="relative flex-1 min-w-[200px]">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search features…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div className="flex flex-wrap gap-1" role="group" aria-label="Show">
                    {([
                      ['all', 'All', features.length],
                      ['included', 'Included', entitled.length],
                      ...(plansReady ? [['exceptions', 'Exceptions', added.length + removed.length] as const] : []),
                      ['not_included', plansReady ? 'Not included' : 'Locked', features.length - entitled.length],
                      ['admin_off', 'Admin turned off', adminOff.length],
                    ] as const).map(([k, label, n]) => (
                      <button
                        key={k}
                        onClick={() => setShow(k)}
                        aria-pressed={show === k}
                        className={`px-2.5 py-1.5 rounded-lg text-[13px] font-medium border ${
                          show === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        {label} <span className={`ml-0.5 text-[11px] ${show === k ? 'text-slate-300' : 'text-slate-400'}`}>{n}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {groups.length === 0 ? (
                  <div className="bg-white rounded-lg border border-slate-200 flex flex-col items-center justify-center py-14 text-center">
                    <p className="text-sm text-slate-500">No features match.</p>
                    <button onClick={() => { setSearch(''); setShow('all') }} className="mt-2 text-xs text-blue-600 hover:underline">Clear filters</button>
                  </div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
                    <table className="w-full text-[13px] border-collapse">
                      <thead>
                        <tr className="text-[11px] uppercase tracking-wider text-slate-400">
                          <th className="text-left font-semibold px-3 py-2.5 bg-slate-50 border-b border-slate-200">Feature</th>
                          <th className="text-left font-semibold px-3 py-2.5 bg-slate-50 border-b border-slate-200 whitespace-nowrap">{plansReady ? 'Included because' : 'Entitlement'}</th>
                          <th className="text-left font-semibold px-3 py-2.5 bg-slate-50 border-b border-slate-200 whitespace-nowrap">Their admin</th>
                          <th className="text-right font-semibold px-3 py-2.5 bg-slate-50 border-b border-slate-200 whitespace-nowrap">Change for this company</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groups.map((g) => (
                          <PlanGroup key={g.key} label={g.label} cols={4}>
                            {g.items.map((f) => {
                              const parent = f.parent_key ? nameOf.get(f.parent_key) : null
                              return (
                                <tr key={f.feature_key} className="border-b border-slate-200 last:border-b-0">
                                  <td className="px-3 py-2.5 align-middle">
                                    <b className="font-semibold text-slate-900">{f.feature_name}</b>
                                    {plansReady && <PlatformTags f={f} />}
                                    {(f.description || parent) && (
                                      <div className="text-xs text-slate-400">
                                        {f.description}
                                        {parent && <>{f.description ? ' · ' : ''}Part of {parent}</>}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2.5 align-middle">{includedBecause(f)}</td>
                                  <td className="px-3 py-2.5 align-middle">{theirAdmin(f)}</td>
                                  <td className="px-3 py-2.5 align-middle text-right whitespace-nowrap">{action(f)}</td>
                                </tr>
                              )
                            })}
                          </PlanGroup>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <div role="status" className={`fixed left-1/2 -translate-x-1/2 bottom-5 z-50 px-4 py-2 rounded-lg text-sm shadow-lg inline-flex items-center gap-2 ${toast.tone === 'error' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'}`}>
          {toast.tone === 'ok' ? <CheckCircleIcon className="w-4 h-4" /> : <ExclamationTriangleIcon className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
    </div>
  )
}
